
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Badge } from "@/components/ui/badge";
import { Medication } from "@/types";
import { Plus } from "lucide-react";
import { MedicationTable } from "./MedicationTable";
import { FrequencyPatternSelector } from "./FrequencyPatternSelector";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface MedicationFormProps {
  medications: Medication[];
  onAddMedication: (medication: Medication) => void;
  onAddMultipleMedications?: (medications: Medication[]) => void;
  onRemoveMedication: (index: number) => void;
  onUpdateMedication?: (medication: Medication, index: number) => void;
}

export function MedicationForm({
  medications,
  onAddMedication,
  onAddMultipleMedications,
  onRemoveMedication,
  onUpdateMedication
}: MedicationFormProps) {
  const { toast } = useToast();
  
  // Enhanced medication profiles - stores complete medication information
  const [savedMedicationProfiles, setSavedMedicationProfiles] = useState<Medication[]>([]);
  
  // Edit state
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  
  // Load saved medication profiles from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('savedMedicationProfiles');
    if (saved) {
      try {
        setSavedMedicationProfiles(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading saved medication profiles:', error);
        setSavedMedicationProfiles([]);
      }
    } else {
      // Migration: Convert old savedMedications to new format
      const oldSaved = localStorage.getItem('savedMedications');
      if (oldSaved) {
        try {
          const oldMedications = JSON.parse(oldSaved);
          const migratedProfiles = oldMedications.map((name: string) => ({
            name,
            dosage: '',
            frequency: '',
            frequencyPattern: '',
            frequencyDescription: '',
            duration: '',
            instructions: ''
          }));
          setSavedMedicationProfiles(migratedProfiles);
          localStorage.setItem('savedMedicationProfiles', JSON.stringify(migratedProfiles));
          localStorage.removeItem('savedMedications'); // Clean up old format
        } catch (error) {
          console.error('Error migrating old medications:', error);
          setSavedMedicationProfiles([]);
        }
      }
    }
  }, []);
  
  // Save complete medication profiles to localStorage
  const saveMedicationProfile = (medication: Medication) => {
    if (!medication.name.trim()) return;
    
    const updatedProfiles = [...savedMedicationProfiles];
    const existingIndex = updatedProfiles.findIndex(profile => 
      profile.name.toLowerCase() === medication.name.toLowerCase()
    );
    
    if (existingIndex >= 0) {
      // Update existing profile with latest usage
      updatedProfiles[existingIndex] = {
        ...medication
      };
    } else {
      // Add new profile
      updatedProfiles.push({
        ...medication
      });
    }
    
    // Sort alphabetically by name
    updatedProfiles.sort((a, b) => a.name.localeCompare(b.name));
    setSavedMedicationProfiles(updatedProfiles);
    localStorage.setItem('savedMedicationProfiles', JSON.stringify(updatedProfiles));
  };
  
  // Get saved medication names for backward compatibility
  const savedMedications = savedMedicationProfiles.map(profile => profile.name);

  // Predefined common instructions
  const predefinedInstructions = [
    "Take after meals",
    "Take on empty stomach",
    "Take with plenty of water",
    "Take before bedtime",
    "Take in the morning",
    "Avoid alcohol while taking this medication",
    "Store in refrigerator",
    "Shake well before use",
    "Take with food to avoid stomach upset",
    "Do not drive or operate machinery",
    "Complete the full course",
    "Take at the same time each day",
    "Avoid exposure to sunlight",
    "Take with milk",
    "Do not take with dairy products",
    "Take 30 minutes before meals",
    "Take 1 hour after meals",
    "Apply to affected area only",
    "Keep out of reach of children",
    "Discontinue if rash appears"
  ];

  // Predefined dose types
  const predefinedDoseTypes = [
    "Tablet",
    "Capsule",
    "mL",
    "mg",
    "gm",
    "Drops",
    "Puff",
    "Sachets",
    "Units",
    "Tspf (Tea spoon)",
    "Injections",
    "Syrup",
    "Cream",
    "Ointment",
    "Gel",
    "Patch",
    "Suppository",
    "Powder",
    "Lotion",
    "Spray"
  ];
  

  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);
  const [highlightedMedicationIndex, setHighlightedMedicationIndex] = useState(-1);
  const [showInstructionsDropdown, setShowInstructionsDropdown] = useState(false);
  const [highlightedInstructionIndex, setHighlightedInstructionIndex] = useState(-1);
  const [showDoseTypeDropdown, setShowDoseTypeDropdown] = useState(false);
  const [highlightedDoseTypeIndex, setHighlightedDoseTypeIndex] = useState(-1);
  
  // Current medication being edited
  const [currentMedication, setCurrentMedication] = useState<Medication>({
    name: "", 
    dosage: "", 
    doseType: "",
    frequency: "", 
    frequencyPattern: "",
    frequencyDescription: "",
    duration: "", 
    instructions: ""
  });

  const handleMedicationChange = (field: keyof Medication, value: string) => {
    setCurrentMedication(prev => ({ ...prev, [field]: value }));
  };
  
  const handleInstructionSelect = (instruction: string) => {
    const currentInstructions = currentMedication.instructions;
    
    // If there are existing instructions, append the new one
    if (currentInstructions && currentInstructions.trim()) {
      setCurrentMedication({
        ...currentMedication,
        instructions: `${currentInstructions}; ${instruction}`
      });
    } else {
      // Set as the first instruction
      setCurrentMedication({
        ...currentMedication,
        instructions: instruction
      });
    }
    
    setShowInstructionsDropdown(false);
    setHighlightedInstructionIndex(-1);
  };
  
  const handleInstructionsChange = (value: string) => {
    setCurrentMedication({ ...currentMedication, instructions: value });
    
    // Show dropdown if there are matching instructions and user is typing
    if (value.trim().length > 0) {
      const matchingInstructions = predefinedInstructions.filter(instruction => 
        instruction.toLowerCase().includes(value.toLowerCase())
      );
      setShowInstructionsDropdown(matchingInstructions.length > 0);
      setHighlightedInstructionIndex(-1); // Reset highlight when typing
    } else {
      setShowInstructionsDropdown(false);
      setHighlightedInstructionIndex(-1);
    }
  };

  const handleInstructionsFocus = () => {
    // Show all instructions when focused, or filtered if there's text
    if (currentMedication.instructions.trim().length > 0) {
      const matchingInstructions = predefinedInstructions.filter(instruction => 
        instruction.toLowerCase().includes(currentMedication.instructions.toLowerCase())
      );
      setShowInstructionsDropdown(matchingInstructions.length > 0);
    } else {
      // Show all instructions when field is empty and focused
      setShowInstructionsDropdown(true);
    }
  };

  const handleInstructionsBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => {
      setShowInstructionsDropdown(false);
      setHighlightedInstructionIndex(-1);
    }, 200);
  };

  const handleInstructionsKeyDown = (e: React.KeyboardEvent) => {
    if (!showInstructionsDropdown) return;

    const matchingInstructions = predefinedInstructions.filter(instruction => 
      instruction.toLowerCase().includes(currentMedication.instructions.toLowerCase())
    );

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedInstructionIndex(prev => 
          prev < matchingInstructions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedInstructionIndex(prev => 
          prev > 0 ? prev - 1 : matchingInstructions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedInstructionIndex >= 0 && highlightedInstructionIndex < matchingInstructions.length) {
          handleInstructionSelect(matchingInstructions[highlightedInstructionIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowInstructionsDropdown(false);
        setHighlightedInstructionIndex(-1);
        break;
    }
  };

  const handleDoseTypeChange = (value: string) => {
    setCurrentMedication({ ...currentMedication, doseType: value });
    
    // Show dropdown if there are matching dose types and user is typing
    if (value.trim().length > 0) {
      const matchingDoseTypes = predefinedDoseTypes.filter(doseType => 
        doseType.toLowerCase().includes(value.toLowerCase())
      );
      setShowDoseTypeDropdown(matchingDoseTypes.length > 0);
      setHighlightedDoseTypeIndex(-1); // Reset highlight when typing
    } else {
      setShowDoseTypeDropdown(false);
      setHighlightedDoseTypeIndex(-1);
    }
  };

  const handleDoseTypeFocus = () => {
    // Show all dose types when focused, or filtered if there's text
    if (currentMedication.doseType && currentMedication.doseType.trim().length > 0) {
      const matchingDoseTypes = predefinedDoseTypes.filter(doseType => 
        doseType.toLowerCase().includes(currentMedication.doseType.toLowerCase())
      );
      setShowDoseTypeDropdown(matchingDoseTypes.length > 0);
    } else {
      // Show all dose types when field is empty and focused
      setShowDoseTypeDropdown(true);
    }
  };

  const handleDoseTypeBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => {
      setShowDoseTypeDropdown(false);
      setHighlightedDoseTypeIndex(-1);
    }, 200);
  };

  const handleDoseTypeKeyDown = (e: React.KeyboardEvent) => {
    if (!showDoseTypeDropdown) return;

    const matchingDoseTypes = predefinedDoseTypes.filter(doseType => 
      doseType.toLowerCase().includes(currentMedication.doseType.toLowerCase())
    );

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedDoseTypeIndex(prev => 
          prev < matchingDoseTypes.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedDoseTypeIndex(prev => 
          prev > 0 ? prev - 1 : matchingDoseTypes.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedDoseTypeIndex >= 0 && highlightedDoseTypeIndex < matchingDoseTypes.length) {
          setCurrentMedication({ ...currentMedication, doseType: matchingDoseTypes[highlightedDoseTypeIndex] });
          setShowDoseTypeDropdown(false);
          setHighlightedDoseTypeIndex(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDoseTypeDropdown(false);
        setHighlightedDoseTypeIndex(-1);
        break;
    }
  };
  
  const handleMedicationSelect = (medication: string) => {
    // Check if we have a saved profile for this medication
    const savedProfile = savedMedicationProfiles.find(profile => 
      profile.name.toLowerCase() === medication.toLowerCase()
    );
    
    if (savedProfile) {
      // Auto-fill all fields with saved profile data
      setCurrentMedication({
        name: savedProfile.name,
        dosage: savedProfile.dosage || '',
        doseType: savedProfile.doseType || '',
        frequency: savedProfile.frequency || '',
        frequencyPattern: savedProfile.frequencyPattern || '',
        frequencyDescription: savedProfile.frequencyDescription || '',
        duration: savedProfile.duration || '',
        instructions: savedProfile.instructions || ''
      });
      
      toast({
        title: "Medication profile loaded",
        description: `Loaded saved profile for ${savedProfile.name}`,
      });
    } else {
      // Just set the name for new medications
      setCurrentMedication({
        ...currentMedication,
        name: medication
      });
    }
    
    setShowMedicationDropdown(false);
  };
  
  const handleMedicationNameChange = (value: string) => {
    setCurrentMedication({ ...currentMedication, name: value });
    
    // Show dropdown if there are matching medications and user is typing
    if (value.trim().length > 0) {
      const matchingMedications = savedMedicationProfiles.filter(profile => 
        profile.name.toLowerCase().includes(value.toLowerCase())
      );
      setShowMedicationDropdown(matchingMedications.length > 0);
      setHighlightedMedicationIndex(-1); // Reset highlight when typing
    } else {
      setShowMedicationDropdown(false);
      setHighlightedMedicationIndex(-1);
    }
  };

  const handleMedicationNameFocus = () => {
    if (currentMedication.name.trim().length > 0) {
      const matchingMedications = savedMedicationProfiles.filter(profile => 
        profile.name.toLowerCase().includes(currentMedication.name.toLowerCase())
      );
      setShowMedicationDropdown(matchingMedications.length > 0);
    }
  };

  const handleMedicationNameBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => {
      setShowMedicationDropdown(false);
      setHighlightedMedicationIndex(-1);
    }, 200);
  };

  const handleMedicationNameKeyDown = (e: React.KeyboardEvent) => {
    if (!showMedicationDropdown) return;

    const matchingMedications = savedMedicationProfiles.filter(profile => 
      profile.name.toLowerCase().includes(currentMedication.name.toLowerCase())
    );

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedMedicationIndex(prev => 
          prev < matchingMedications.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedMedicationIndex(prev => 
          prev > 0 ? prev - 1 : matchingMedications.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedMedicationIndex >= 0 && highlightedMedicationIndex < matchingMedications.length) {
          handleMedicationSelect(matchingMedications[highlightedMedicationIndex].name);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowMedicationDropdown(false);
        setHighlightedMedicationIndex(-1);
        break;
    }
  };
  
  const handleEditMedication = (medication: Medication, index: number) => {
    // Load the medication data into the form for editing
    setCurrentMedication({
      name: medication.name || "",
      dosage: medication.dosage || "",
      doseType: medication.doseType || "",
      frequency: medication.frequency || "",
      frequencyPattern: medication.frequencyPattern || "",
      frequencyDescription: medication.frequencyDescription || "",
      duration: medication.duration || "",
      instructions: medication.instructions || ""
    });
    setEditingIndex(index);
    
    toast({
      title: "Editing medication",
      description: `Editing ${medication.name}. Make your changes and click "Update Medication" to save.`
    });
  };
  
  const handleAddMedication = () => {
    // Validate medication has minimum required fields
    if (!currentMedication.name || !currentMedication.dosage) {
      toast({
        title: "Missing information",
        description: "Medication name and dosage are required",
        variant: "destructive"
      });
      return;
    }
    
    // Validate duration is a valid number
    if (currentMedication.duration) {
      const durationNum = parseInt(currentMedication.duration);
      if (isNaN(durationNum) || durationNum < 1 || durationNum > 365) {
        toast({
          title: "Invalid duration",
          description: "Duration must be a number between 1 and 365 days",
          variant: "destructive"
        });
        return;
      }
    }
    
    if (editingIndex !== null) {
      // Editing existing medication - replace it
      if (onUpdateMedication) {
        onUpdateMedication({ ...currentMedication }, editingIndex);
      } else {
        // Fallback: remove and add (might cause issues with indices)
        onRemoveMedication(editingIndex);
        onAddMedication({ ...currentMedication });
      }
      
      toast({
        title: "Medication updated",
        description: `${currentMedication.name} has been updated`
      });
      
      setEditingIndex(null);
    } else {
      // Adding new medication
      // Save complete medication profile for future use
      saveMedicationProfile(currentMedication);
      
      // Add to medications list
      const medicationToAdd: Medication = {
        name: currentMedication.name.trim(),
        dosage: currentMedication.dosage.trim(),
        doseType: currentMedication.doseType.trim(),
        frequency: currentMedication.frequency.trim(),
        frequencyPattern: currentMedication.frequencyPattern || "",
        frequencyDescription: currentMedication.frequencyDescription || "",
        duration: currentMedication.duration.trim(),
        instructions: currentMedication.instructions.trim()
      };
      
      
      onAddMedication(medicationToAdd);
      
      toast({
        title: "Medication added",
        description: "The medication has been added to the prescription"
      });
    }
    
    // Clear form for next entry
    setCurrentMedication({
      name: "", 
      dosage: "", 
      doseType: "",
      frequency: "", 
      frequencyPattern: "",
      frequencyDescription: "",
      duration: "", 
      instructions: ""
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium">Medications</h3>
      </div>
      
      {/* Medication entry form - moved to top */}
      <div className="border rounded-md p-4 mb-6">
        <h4 className="font-medium mb-4">Add New Medication</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-2">
            <Label htmlFor="medication-name">Medication Name</Label>
            <div className="relative">
              <Input 
                id="medication-name"
                value={currentMedication.name}
                onChange={(e) => handleMedicationNameChange(e.target.value)}
                onFocus={handleMedicationNameFocus}
                onBlur={handleMedicationNameBlur}
                onKeyDown={handleMedicationNameKeyDown}
                placeholder="Type medication name..."
                className="w-full"
              />
              
              {/* Auto-search dropdown - opens upward */}
              {showMedicationDropdown && (
                <div 
                  className="absolute z-50 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
                  role="listbox"
                  aria-label="Medication options"
                >
                  {savedMedicationProfiles
                    .filter((profile) => 
                      profile.name.toLowerCase().includes(currentMedication.name.toLowerCase())
                    )
                    .map((profile, index) => (
                      <div
                        key={profile.name}
                        className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                          index === highlightedMedicationIndex 
                            ? 'bg-blue-100 text-blue-900' 
                            : 'hover:bg-gray-100'
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent input blur
                          handleMedicationSelect(profile.name);
                        }}
                        role="option"
                        aria-selected={index === highlightedMedicationIndex}
                        tabIndex={-1}
                      >
                        <div className="flex items-center">
                          <span className="font-medium text-sm">{profile.name}</span>
                        </div>
                        {profile.dosage && (
                          <div className="text-xs text-gray-500 mt-1">
                            {profile.dosage} • {profile.frequency || 'No frequency'} • {profile.duration || 'No duration'}
                            {profile.instructions && ` • ${profile.instructions}`}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="medication-dosage">Dosage</Label>
            <Input 
              id="medication-dosage"
              value={currentMedication.dosage}
              onChange={(e) => handleMedicationChange('dosage', e.target.value)}
              placeholder="e.g., 500"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="medication-dose-type">Dose Type</Label>
            <div className="relative">
              <Input 
                id="medication-dose-type"
                value={currentMedication.doseType}
                onChange={(e) => handleDoseTypeChange(e.target.value)}
                onFocus={handleDoseTypeFocus}
                onBlur={handleDoseTypeBlur}
                onKeyDown={handleDoseTypeKeyDown}
                placeholder="e.g., mg, tablet, capsule..."
                className="w-full"
              />
              
              {/* Auto-search dropdown - opens upward */}
              {showDoseTypeDropdown && (
                <div 
                  className="absolute z-50 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
                  role="listbox"
                  aria-label="Dose type options"
                >
                  {predefinedDoseTypes
                    .filter(doseType => 
                      doseType.toLowerCase().includes(currentMedication.doseType.toLowerCase())
                    )
                    .map((doseType, index) => (
                      <div
                        key={doseType}
                        className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                          index === highlightedDoseTypeIndex 
                            ? 'bg-blue-100 text-blue-900' 
                            : 'hover:bg-gray-100'
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent input blur
                          setCurrentMedication({ ...currentMedication, doseType });
                          setShowDoseTypeDropdown(false);
                          setHighlightedDoseTypeIndex(-1);
                        }}
                        role="option"
                        aria-selected={index === highlightedDoseTypeIndex}
                        tabIndex={-1}
                      >
                        <span className="text-sm">{doseType}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <FrequencyPatternSelector
              value={currentMedication.frequency}
              patternValue={currentMedication.frequencyPattern || ''}
              descriptionValue={currentMedication.frequencyDescription || ''}
              onChange={(frequency, pattern, description) => {
                console.log('MedicationForm: FrequencyPatternSelector onChange called with:', {
                  frequency,
                  pattern,
                  description
                });
                handleMedicationChange('frequency', frequency);
                handleMedicationChange('frequencyPattern', pattern || "");
                handleMedicationChange('frequencyDescription', description || "");
                console.log('MedicationForm: After handleMedicationChange calls, currentMedication:', currentMedication);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="medication-duration">Duration (Days)</Label>
            <Input 
              id="medication-duration"
              type="number"
              min="1"
              max="365"
              value={currentMedication.duration}
              onChange={(e) => handleMedicationChange('duration', e.target.value)}
              placeholder="e.g., 7"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="medication-instructions">Special Instructions</Label>
            <div className="relative">
              <Textarea 
                id="medication-instructions"
                value={currentMedication.instructions}
                onChange={(e) => handleInstructionsChange(e.target.value)}
                onFocus={handleInstructionsFocus}
                onBlur={handleInstructionsBlur}
                onKeyDown={handleInstructionsKeyDown}
                placeholder="Type instructions or select from options below..."
                className="resize-none h-20 w-full"
              />
              
              {/* Auto-search dropdown - opens upward */}
              {showInstructionsDropdown && (
                <div 
                  className="absolute z-50 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
                  role="listbox"
                  aria-label="Instruction options"
                >
                  {predefinedInstructions
                    .filter(instruction => 
                      instruction.toLowerCase().includes(currentMedication.instructions.toLowerCase())
                    )
                    .map((instruction, index) => (
                      <div
                        key={instruction}
                        className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                          index === highlightedInstructionIndex 
                            ? 'bg-blue-100 text-blue-900' 
                            : 'hover:bg-gray-100'
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent input blur
                          handleInstructionSelect(instruction);
                        }}
                        role="option"
                        aria-selected={index === highlightedInstructionIndex}
                        tabIndex={-1}
                      >
                        <span className="text-sm">{instruction}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mt-6">
          <Button 
            onClick={handleAddMedication}
            disabled={!currentMedication.name || !currentMedication.dosage}
            className="h-10 px-8"
          >
            <Plus className="h-4 w-4 mr-1" />
            {editingIndex !== null ? "Update Medication" : "Add Medication"}
          </Button>
        </div>
      </div>
      
      {/* Display the added medications - moved below form */}
      <div>
        <MedicationTable 
          medications={medications} 
          onDelete={onRemoveMedication}
          onLoadTemplate={(templateMedications) => {
            // Validate all medications first
            const validatedMedications: Medication[] = templateMedications.map((medication) => {
              const validatedMedication: Medication = {
                name: medication.name || '',
                dosage: medication.dosage || '',
                doseType: medication.doseType || '',
                frequency: medication.frequency || '',
                frequencyPattern: medication.frequencyPattern || '',
                frequencyDescription: medication.frequencyDescription || '',
                duration: medication.duration || '',
                instructions: medication.instructions || ''
              };
              return validatedMedication;
            });
            
            // Add all medications at once if bulk function is available, otherwise add individually
            if (onAddMultipleMedications) {
              onAddMultipleMedications(validatedMedications);
            } else {
              validatedMedications.forEach(medication => {
                onAddMedication(medication);
              });
            }
          }}
          onEditMedication={handleEditMedication}
        />
      </div>
    </div>
  );
}
